import React, { useEffect, useState } from "react";
import { AlertTriangle, ArrowRightLeft, PowerOff, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useResourceMutations } from "@/hooks/useResource";

// Generic safe-delete confirm for resources whose deletion may break linked offers.
// resource: "advertisers" | "products"; item: the row (uses item.active_offers).
export function SafeDeleteDialog({ open, onOpenChange, resource, item, transferOptions = [], label }) {
  const { remove } = useResourceMutations(resource);
  const activeCount = item?.active_offers || 0;
  const noun = label || (resource === "advertisers" ? "advertiser" : "product");
  const [mode, setMode] = useState("transfer");
  const [target, setTarget] = useState("");

  useEffect(() => {
    if (open) { setMode("transfer"); setTarget(""); }
  }, [open, item?.id]);

  const targets = (transferOptions || []).filter((o) => o.value !== item?.id);

  const confirm = async () => {
    try {
      if (activeCount === 0) {
        await remove.mutateAsync(item.id);
      } else if (mode === "transfer") {
        if (!target) return;
        await remove.mutateAsync({ id: item.id, params: { mode: "transfer", target_id: target } });
      } else {
        await remove.mutateAsync({ id: item.id, params: { mode: "disable" } });
      }
      onOpenChange(false);
    } catch {
      /* toast handled by mutation */
    }
  };

  const disabled = remove.isPending || (activeCount > 0 && mode === "transfer" && !target);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/[0.1] bg-[#141417]" data-testid="safe-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <AlertTriangle className="h-4 w-4 text-[#F59E0B]" /> Delete {noun}?
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {item?.name}
          </DialogDescription>
        </DialogHeader>

        {activeCount === 0 ? (
          <p className="text-sm text-muted-foreground">
            This {noun} has no active offers. This action cannot be undone.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-foreground">
              This {noun} is linked to <span className="font-semibold text-[#F59E0B]">{activeCount}</span> active offer{activeCount === 1 ? "" : "s"}.
              Choose what to do before deleting:
            </p>
            <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-white/[0.1] bg-[#0A0A0A] p-3 hover:bg-white/[0.03]">
                <RadioGroupItem value="transfer" id="sd-transfer" data-testid="safe-delete-transfer" className="mt-0.5" />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <ArrowRightLeft className="h-3.5 w-3.5" /> Transfer offers to another {noun}
                  </div>
                  {mode === "transfer" && (
                    <Select value={target} onValueChange={setTarget}>
                      <SelectTrigger data-testid="safe-delete-target" className="h-9 border-white/[0.1] bg-[#0A0A0A] text-sm">
                        <SelectValue placeholder={`Select target ${noun}...`} />
                      </SelectTrigger>
                      <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">
                        {targets.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-white/[0.1] bg-[#0A0A0A] p-3 hover:bg-white/[0.03]">
                <RadioGroupItem value="disable" id="sd-disable" data-testid="safe-delete-disable" className="mt-0.5" />
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <PowerOff className="h-3.5 w-3.5" /> Disable all offers, then delete
                </div>
              </label>
            </RadioGroup>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="safe-delete-cancel" className="text-muted-foreground">
            Cancel
          </Button>
          <Button onClick={confirm} disabled={disabled} data-testid="safe-delete-confirm"
            className="gap-1.5 bg-[#EF4444] text-white hover:bg-[#dc2626]">
            <Trash2 className="h-4 w-4" /> {remove.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
